import { useState, useDeferredValue } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Flag, Plus } from 'lucide-react';
import type { PaginationState } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import type { PatientResponse } from '@vagusx/clients';
import { usePatients } from '@/hooks/use-patients';
import { formatPhone, formatDate, calculateAge } from '@/lib/format';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import { NewPatientDialog } from './NewPatientDialog';

const columns: ColumnDef<PatientResponse>[] = [
  {
    accessorKey: 'name',
    accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span className="font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
        {row.original.flagged === 1 && <Flag className="h-3.5 w-3.5 text-red-500 fill-red-500" />}
      </div>
    ),
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    cell: ({ row }) => formatPhone(row.original.phone),
  },
  {
    accessorKey: 'dateOfBirth',
    header: ({ column }) => <SortableHeader column={column}>Date of Birth</SortableHeader>,
    cell: ({ row }) => {
      const dob = row.original.dateOfBirth;
      if (!dob) return null;
      const age = calculateAge(dob);
      return (
        <span>
          {formatDate(dob)} <span className="text-gray-500">(age: {age} yrs)</span>
        </span>
      );
    },
  },
];

export default function PatientsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewPatient, setShowNewPatient] = useState(false);
  const deferredSearch = useDeferredValue(searchTerm);

  const { data, isLoading, error } = usePatients({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    searchTerm: deferredSearch || undefined,
  });

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <TablePage
      title="Patients"
      description="View patient records"
      headerAction={
        <Button size="sm" className="gap-1.5" onClick={() => setShowNewPatient(true)}>
          <Plus className="h-4 w-4" />
          New patient
        </Button>
      }
      columns={columns}
      data={data?.items ?? []}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      searchPlaceholder="Search by name, DOB, MRN, phone, or insurance ID..."
      onRowClick={(patient) => navigate({ to: '/patients/$id', params: { id: patient.id } })}
      emptyMessage="No patients found."
      errorMessage="Failed to load patients. Please try again."
      pagination={pagination}
      onPaginationChange={setPagination}
      onSearchChange={handleSearchChange}
    >
      <NewPatientDialog open={showNewPatient} onOpenChange={setShowNewPatient} />
    </TablePage>
  );
}
